// Importing useState hook  from React for state management
import { useState } from 'react';
// Importing useNavigate hook from react-router-dom to programmatically navigate between routes
import { useNavigate } from 'react-router-dom';
// Importing a pre-configured Axios instance for making HTTP requests to the backend API
import axiosClient from '../../api/axiosClient';

function Signup() {
  // State for form inputs
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    real_name: '',
    role: 'student', // default role
    password1: '',
    password2: '',
  });
  // State for error messages
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate(); // React Router navigation hook
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); // Prevent default form submission
    try {
      // Fetch CSRF token from backend
      const response = await axiosClient.get('/elearning/get-csrf/');
      const csrfToken = response.data.csrfToken;
      if (!csrfToken) throw new Error('CSRF token not available');
      // Send signup POST request to backend with CSRF token
      await axiosClient.post('/elearning/api/signup/', formData, {
        headers: { 'X-CSRFToken': csrfToken },
      });
      // Navigate to home page on successful signup
      navigate('/');
    } catch (err: any) {
      // Handle errors from API or network
      const errorMessage = err.response?.data?.message || 'Signup failed. Please try again.';
      console.error('[Signup] Error:', errorMessage);
      setError(errorMessage); // Display error to user
    }
  };
  // Handle input changes for both text and select fields
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  return (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
    <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
      {/* Page heading */}
      <h1 className="text-3xl font-extrabold text-gray-900 mb-6 text-center">
        Sign Up
      </h1>

      {/* Display error messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg shadow-sm">
          {error}
        </div>
      )}

      {/* Signup form */}
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <input
          type="text"
          name="username"
          placeholder="Username"
          value={formData.username}
          onChange={handleChange}
          className="block w-full rounded-lg border border-gray-300 p-3 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-base font-semibold"
          autoComplete="username"
          required
        />
        <input
          type="email"
          name="email"
          placeholder="Email"
          value={formData.email}
          onChange={handleChange}
          className="block w-full rounded-lg border border-gray-300 p-3 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-base font-semibold"
          autoComplete="email"
          required
        />
        <input
          type="text"
          name="real_name"
          placeholder="Full Name"
          value={formData.real_name}
          onChange={handleChange}
          className="block w-full rounded-lg border border-gray-300 p-3 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-base font-semibold"
        />
        <select
          name="role"
          value={formData.role}
          onChange={handleChange}
          className="block w-full rounded-lg border border-gray-300 p-3 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-base font-semibold"
          required
        >
          <option value="student">Student</option>
          <option value="teacher">Teacher</option>
        </select>
        <input
          type="password"
          name="password1"
          placeholder="Password"
          value={formData.password1}
          onChange={handleChange}
          className="block w-full rounded-lg border border-gray-300 p-3 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-base font-semibold"
          autoComplete="new-password"
          required
        />
        <input
          type="password"
          name="password2"
          placeholder="Confirm Password"
          value={formData.password2}
          onChange={handleChange}
          className="block w-full rounded-lg border border-gray-300 p-3 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-base font-semibold"
          autoComplete="new-password"
          required
        />

        {/* Sign Up button */}
        <button
          type="submit"
          className="w-full text-white font-medium rounded-lg p-3 shadow bg-blue-600 hover:bg-blue-700 transition"
        >
          Sign Up
        </button>
      </form>

      {/* Login button for existing users */}
      <div className="mt-4 text-center">
        <p className="text-gray-600 mb-2">Already have an account?</p>
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="w-full text-white font-medium rounded-lg p-3 shadow bg-green-600 hover:bg-green-700 transition"
        >
          Login
        </button>
      </div>
    </div>
  </div>
);

}
export default Signup;
